/**
 * bg-removal-pricing.js
 * Ashe Design – pricing dropdowns on the Remove Background page.
 *
 * When the customer picks a credit amount:
 *   - shows that option's price and price per image
 *   - points the card's buy button at that option's Stripe price id
 *   - an option with no price id shows the button as "Coming soon"
 *
 * Works for everyone, logged in or not. For logged-in subscribers it also
 * pre-selects their current plan once their account loads.
 *
 * Works with bg-removal.js through events on [data-bgr-root]:
 *   listens for  "bgr:account"         (latest account data)
 *   sends        "bgr:pricing-change"  (a dropdown changed; relabel buttons)
 */
(() => {
  'use strict';

  const UNAVAILABLE_LABEL = 'Coming soon';

  // The buy buttons stay off until bg-removal.js has loaded the account
  // (it sets aria-busy="false" on the page root when they're ready).
  const accountReady = (root) => root.getAttribute('aria-busy') === 'false';

  function initCard(root, card) {
    const select = card.querySelector('[data-bgr-pricing-select]');
    if (!select) return null;
    const priceNode = card.querySelector('[data-bgr-pricing-price]');
    const perNode = card.querySelector('[data-bgr-pricing-per]');
    // Only a logged-in customer's button has data-bgr-kind; others are links.
    const button = card.querySelector('[data-bgr-kind]');

    function updateButton(priceId) {
      if (!button) return;
      button.dataset.bgrPrice = priceId;
      if (priceId) {
        button.removeAttribute('data-bgr-unavailable');
        button.disabled = !accountReady(root);
        if (button.dataset.bgrLabel) button.textContent = button.dataset.bgrLabel;
      } else {
        button.setAttribute('data-bgr-unavailable', '');
        button.disabled = true;
        button.textContent = UNAVAILABLE_LABEL;
      }
    }

    function apply() {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      const { credits = '', price = '', per = '' } = option.dataset;
      if (priceNode) priceNode.textContent = price;
      if (perNode) perNode.textContent = per;
      card.dataset.bgrCredits = credits;
      updateButton(option.value.trim());
      root.dispatchEvent(new CustomEvent('bgr:pricing-change', { detail: { card } }));
    }

    // Picks the option for a number of credits, if there is one.
    function selectCredits(credits) {
      const match = Array.from(select.options).findIndex(
        (option) => Number(option.dataset.credits) === Number(credits)
      );
      if (match === -1) return false;
      select.selectedIndex = match;
      apply();
      return true;
    }

    select.addEventListener('change', apply);
    apply();

    return {
      apply,
      selectCredits,
      isMonthly: Boolean(card.querySelector('[data-bgr-kind="subscription"]')),
    };
  }

  function initPricing(root) {
    if (root.dataset.bgrPricingReady === 'true') return;
    root.dataset.bgrPricingReady = 'true';

    const cards = Array.from(root.querySelectorAll('[data-bgr-pricing-card]'))
      .map((card) => initCard(root, card))
      .filter(Boolean);
    if (!cards.length) return;

    // Once per visit: open the monthly dropdown on the customer's own plan.
    let preselected = false;
    function preselectPlan(account) {
      if (preselected || !account) return;
      preselected = true;
      const plan = account.plan;
      if (!plan) return;
      const monthly = cards.find((c) => c.isMonthly);
      if (monthly) monthly.selectCredits(plan.creditsPerMonth);
    }

    root.addEventListener('bgr:account', (event) => preselectPlan(event.detail));
    if (root.bgrAccount) preselectPlan(root.bgrAccount);

    // Back button: the browser may restore a different dropdown choice.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) cards.forEach((c) => c.apply());
    });
  }

  function boot(scope = document) {
    scope.querySelectorAll('[data-bgr-root]').forEach((root) => {
      try {
        initPricing(root);
      } catch (error) {
        console.error('[bg-removal-pricing] Startup failed:', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
})();
